import { BrandEnum } from '@infrastructure/brand.enum';
import { Type } from 'class-transformer';
import { ProductlineConstants } from './../shared/productline-constants';
import { ModelModel } from './api-models/model';
import { ModelgroupOrderApimodel } from './api-models/modelgroup-order-apimodel';
import { AssortmentLineModel } from './assortment-line-model';
import { AvailabilityModel } from './availability-model';
import { DataTranslationModel } from './data-translation-model';
import { OrderItemModel } from './order-item';

export class ModelGroupModel extends AvailabilityModel {
	Id: number;
	Uuid: string;
	Code?: string;

	@Type(() => DataTranslationModel)
	Description: DataTranslationModel | null;

	@Type(() => DataTranslationModel)
	ShortDescription: DataTranslationModel | null;
	KidTeen: boolean;
	Status: number;
	IsRimless?: boolean;
	SortDescription: string;
	FullFrame?: boolean;
	ParentModelGroupId?: number;
	CanOrder: boolean;
	ProductLineCode?: string;
	@Type(() => DataTranslationModel)
	ProductLineDescription: DataTranslationModel | null;
	ProductLineUuid: string;
	ParentModelGroupUuid?: string;

	@Type(() => DataTranslationModel)
	ParentModelGroupDescription: DataTranslationModel | null;

	@Type(() => DataTranslationModel)
	ParentModelGroupShortDescription: DataTranslationModel | null;
	HasForms: boolean;
	HasFixtures: boolean;
	Category?: string;

	@Type(() => ModelGroupModel)
	ModelGroupChildren: Array<ModelGroupModel | null>;

	@Type(() => AssortmentLineModel)
	AssortmentLines: Array<AssortmentLineModel>;

	@Type(() => ModelModel)
	DefaultModel: ModelModel | null;
	Brand: BrandEnum;

	@Type(() => DataTranslationModel)
	PreciousDescription: DataTranslationModel | null;
	HitCount: number;
	Selected: boolean;

	get getModelGroupShortDescription(): DataTranslationModel | null {
		return this.ShortDescription?.Fallback ? this.ShortDescription : this.Description;
	}

	// WTF: THis should be based on a DB setting ("Fixed") - NOT HARD CODED to a specific Product Line. I have added "code == att" as a consequence
	public get IsConfigurationBased() {
		return this.ProductLineCode == ProductlineConstants.ACETANIUM || this.Code == 'ATT';
	}

	public get IsBothConfigAndVariantBased() {
		return this.ProductLineCode == ProductlineConstants.SUN
			|| this.ProductLineCode == ProductlineConstants.RIM
			|| this.ProductLineCode == ProductlineConstants.SPIRIT;
	}

	public get modelGroupDataUuid() {
		return this.ParentModelGroupUuid || this.Uuid; // If modelgroup has a parent, the parent is responsible for the data
	}

	public get modelGroupId() {
		return this.ParentModelGroupId || this.Id; // If modelgroup has a parent, the parent is responsible for the data
	}

	public get FullFrameChildModelGroup() {
		return this.ModelGroupChildren?.find(x => x?.FullFrame);
	}

	public static createCopy(fromModel: ModelGroupModel | null): ModelGroupModel | null {
		if (fromModel) {
			const newModel = new ModelGroupModel(fromModel.Discontinued ? new Date(fromModel.Discontinued) : undefined, fromModel.New);
			newModel.Id = fromModel.Id;
			newModel.Uuid = fromModel.Uuid;
			newModel.Code = fromModel.Code;
			newModel.Description = fromModel.Description ? new DataTranslationModel(fromModel.Description.TranslationKey, fromModel.Description.Fallback) : null;
			newModel.ShortDescription = fromModel.ShortDescription ? new DataTranslationModel(fromModel.ShortDescription.TranslationKey, fromModel.ShortDescription.Fallback) : null;
			newModel.KidTeen = fromModel.KidTeen;
			newModel.Status = fromModel.Status;
			newModel.IsRimless = fromModel.IsRimless;
			newModel.SortDescription = fromModel.SortDescription;
			newModel.FullFrame = fromModel.FullFrame;
			newModel.ParentModelGroupId = fromModel.ParentModelGroupId;
			newModel.CanOrder = fromModel.CanOrder;
			newModel.ProductLineCode = fromModel.ProductLineCode;
			newModel.ProductLineDescription = fromModel.ProductLineDescription ? new DataTranslationModel(fromModel.ProductLineDescription.TranslationKey, fromModel.ProductLineDescription.Fallback) : null;
			newModel.ProductLineUuid = fromModel.ProductLineUuid;
			newModel.ParentModelGroupUuid = fromModel.ParentModelGroupUuid;
			newModel.ParentModelGroupDescription = fromModel.ParentModelGroupDescription ? new DataTranslationModel(fromModel.ParentModelGroupDescription.TranslationKey, fromModel.ParentModelGroupDescription.Fallback) : null;
			newModel.ParentModelGroupShortDescription = fromModel.ParentModelGroupShortDescription ? new DataTranslationModel(fromModel.ParentModelGroupShortDescription.TranslationKey, fromModel.ParentModelGroupShortDescription.Fallback) : null;
			newModel.HasForms = fromModel.HasForms;
			newModel.HasFixtures = fromModel.HasFixtures;
			newModel.Category = fromModel.Category;
			newModel.Brand = fromModel.Brand;
			newModel.PreciousDescription = fromModel.PreciousDescription ? new DataTranslationModel(fromModel.PreciousDescription.TranslationKey, fromModel.PreciousDescription.Fallback) : null;

			newModel.ModelGroupChildren = fromModel.ModelGroupChildren?.map(x => ModelGroupModel.createCopy(x));
			newModel.AssortmentLines = fromModel.AssortmentLines?.map(x => AssortmentLineModel.createCopy(x));
			newModel.DefaultModel = ModelModel.createCopy(fromModel.DefaultModel);

			return newModel;
		} else {
			return null;
		}

	}

	public static mapFromApiModel(fromModel: ModelgroupOrderApimodel): ModelGroupModel | null {
		if (fromModel) {
			const newModel = new ModelGroupModel(fromModel.Discontinued ? new Date(fromModel.Discontinued) : undefined, fromModel.New);
			newModel.Id = fromModel.Id;
			newModel.Uuid = fromModel.Uuid;
			newModel.Code = fromModel.Code;
			newModel.Description = fromModel.Description ? new DataTranslationModel(fromModel.Description.TranslationKey, fromModel.Description.Fallback) : null;
			newModel.ShortDescription = fromModel.ShortDescription ? new DataTranslationModel(fromModel.ShortDescription.TranslationKey, fromModel.ShortDescription.Fallback) : null;
			newModel.KidTeen = fromModel.KidTeen;
			newModel.Status = fromModel.Status;
			newModel.IsRimless = fromModel.IsRimless;
			newModel.SortDescription = fromModel.SortDescription;
			newModel.FullFrame = fromModel.FullFrame;
			newModel.ParentModelGroupId = fromModel.ParentModelGroupId;
			newModel.ParentModelGroupUuid = fromModel.ParentModelGroupUuid;
			newModel.ProductLineCode = fromModel.ProductLineCode;
			newModel.ProductLineUuid = fromModel.ProductLineUuid;
			newModel.HasFixtures = fromModel.HasFixtures;
			newModel.HasForms = fromModel.HasForms;
			newModel.Category = fromModel.Category;

			newModel.AssortmentLines = fromModel.AssortmentLines?.map(x => AssortmentLineModel.createCopy(x));

			return newModel;
		}
		else {
			return null;
		}
	}

	public static mapFromStoreModel(fromModel: OrderItemModel): ModelGroupModel | null {
		if (fromModel) {
			const newModel = new ModelGroupModel(undefined, fromModel.New);
			newModel.Id = fromModel.Id;
			newModel.Uuid = fromModel.Uuid;
			newModel.Code = fromModel.ModelCode;
			newModel.IsRimless = fromModel.IsRimless;
			newModel.FullFrame = fromModel.IsFullFrame;
			newModel.ProductLineCode = fromModel.ProductLineCode;
			newModel.ProductLineUuid = fromModel.ProductLineUuid ?? 'NA';
			newModel.HasFixtures = fromModel.HasFixtures;
			newModel.HasForms = fromModel.HasForms;
			newModel.Brand = fromModel.Brand;

			return newModel;
		} else {
			return null;
		}
	}

}
